import { AnsweringClient } from "../../clients/answeringClient";
import { IManagedUser } from "../types";

export interface ManagedUsersControllerDelegate {
  didGetUsers(users: IManagedUser[]): void;
  filter({
    page,
    total,
    filter,
  }: {
    page: number;
    total: number;
    filter: string;
  }): void;
  applicationShouldLogout(): void;
  genericError(): void;
  didGetAssignmentToken(token: string): void;
}

export class ManagedUsersController {
  constructor(
    private delegate: ManagedUsersControllerDelegate,
    private readonly answeringClient = new AnsweringClient()
  ) {}

  public async getManagedUsers(
    accessToken: string,
    options?: { page: number; limit: number }
  ) {
    try {
      const response = await this.answeringClient.getManagedUsers(
        accessToken,
        options
      );
      const users: IManagedUser[] = response.data.data.map((userData: any) => {
        const userCopy = userData;
        userCopy.assignments = userCopy.assignments.map(
          (assignmentData: any) => {
            const assignmentCopy = assignmentData;
            assignmentCopy.dueDate =
              assignmentCopy.dueDate && new Date(assignmentCopy.dueDate);
            assignmentCopy.startedAt =
              assignmentCopy.startedAt && new Date(assignmentCopy.startedAt);
            assignmentCopy.finishedAt =
              assignmentCopy.finishedAt && new Date(assignmentCopy.finishedAt);
            return assignmentCopy;
          }
        );
        return userCopy;
      });

      this.delegate.didGetUsers(users);
      this.delegate.filter({
        page: response.data.page,
        total: response.data.total,
        filter: response.data.filter || "",
      });
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        this.getManagedUsers(newTokens.accessToken);
        return;
      }
      this.delegate.genericError();
    }
  }

  public async getAssignmentFeedbackToken(
    accessToken: string,
    assignmentId: number
  ) {
    try {
      const response = await this.answeringClient.getAssignmentFeedbackToken(
        accessToken,
        assignmentId
      );
      const token = response.data.token;
      this.delegate.didGetAssignmentToken(token);
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        this.getAssignmentFeedbackToken(newTokens.accessToken, assignmentId);
        return;
      }
      this.delegate.genericError();
    }
  }

  private async refreshTokens(): Promise<{
    accessToken: string;
    refreshToken: string;
  } | void> {
    const refreshToken = localStorage.getItem("RefreshToken");
    if (!refreshToken) {
      this.delegate.applicationShouldLogout();
      return;
    }
    try {
      const response = await this.answeringClient.refreshToken(refreshToken);
      const tokens: { accessToken: string; refreshToken: string } =
        response.data;
      localStorage.setItem("AccessToken", tokens.accessToken);
      localStorage.setItem("RefreshToken", tokens.refreshToken);
      return tokens;
    } catch (error: any) {
      this.delegate.applicationShouldLogout();
      return;
    }
  }
}
